import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/inscripcionTorneo.css";

export default function InscripcionTorneo() {
  const { token } = useAuth();
  const { torneoId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // Controla el paso actual
  const [jugadorLogueado, setJugadorLogueado] = useState(null);
  const [compañero, setCompañero] = useState(null);
  const [dniCompañero, setDniCompañero] = useState("");
  const [torneo, setTorneo] = useState(null);
  const [formaPago, setFormaPago] = useState("");
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [error, setError] = useState("");
  const [fechasYHorarios, setFechasYHorarios] = useState({});


  useEffect(() => {
    // Cargar datos del torneo y jugador logueado
    api.get(`/torneos/${torneoId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setTorneo(res.data);
        setFechasDisponibles(generarDiasDisponibles(res.data.fecha_inicio, res.data.fecha_fin));
      })
      .catch(() => setError("Error al cargar el torneo"));

    api.get("/perfil", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setJugadorLogueado(res.data))
      .catch(() => setError("Error al cargar tus datos"));
  }, [torneoId, token]);

  const formatearFecha = (fechaISO) => {
  if (!fechaISO) return "";
  const fecha = new Date(fechaISO);
  return fecha.toLocaleDateString(); // Esto convierte la fecha al formato local (e.g., "14/03/2026").
};

  const generarDiasDisponibles = (fechaInicio, fechaFin) => {
    const diasPermitidos = [2, 3, 4, 5]; // Martes (2) a Viernes (5)
    const dias = [];
    let fechaActual = new Date(fechaInicio);
    const fechaFinal = new Date(fechaFin);

    while (fechaActual <= fechaFinal) {
      if (diasPermitidos.includes(fechaActual.getDay())) {
        dias.push(new Date(fechaActual));
      }
      fechaActual.setDate(fechaActual.getDate() + 1);
    }

    return dias;
  };

const manejarSeleccionDia = (fecha) => {
  setFechasYHorarios((prev) => {
    const nuevoEstado = { ...prev };
    if (nuevoEstado[fecha]) {
      // Si la fecha ya está seleccionada, la deseleccionamos
      delete nuevoEstado[fecha];
    } else if (Object.keys(nuevoEstado).length < 2) {
      // Si no está seleccionada y hay menos de 2 fechas, la agregamos
      nuevoEstado[fecha] = ""; // Inicializamos el horario vacío
    } else {
      setError("Solo puedes seleccionar hasta 2 días.");
    }
    return nuevoEstado;
  });
};

const manejarSeleccionHorario = (fecha, horario) => {
  setFechasYHorarios((prev) => ({
    ...prev,
    [fecha]: horario,
  }));
};


const verificarInscripcion = async () => {
    try {
      const res = await api.get(`/inscripciones/verificar/${torneoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.inscrito) {
        setError("Ya estás inscrito en este torneo o tu pareja ya está inscrita.");
        return false;
      }
      return true;
    } catch (err) {
      setError("Error al verificar la inscripción.");
      return false;
    }
  };

  


  const buscarCompañero = async () => {
    setError("");
    setCompañero(null);

    try {
      const res = await api.get(`/jugadores/dni/${dniCompañero}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCompañero(res.data);
    } catch (err) {
      setError(err.response?.data?.mensaje || "Error al buscar compañero.");
    }
  };

const confirmarCompañero = async () => {
  if (!compañero) {
    setError("Debes seleccionar un compañero válido.");
    return;
  }

  // Validar que los "Libres" no puedan inscribirse en torneos de "Damas"
  if (jugadorLogueado.genero === "Libres" && torneo.genero === "Damas") {
    setError("Los jugadores de categoría 'Libres' no pueden inscribirse en torneos de 'Damas'.");
    return;
  }

  // Validar que las categorías sean respetadas o que el jugador elija una categoría mayor
  const ordenCategoriasLibres = ["Suma8", "5ta", "6ta", "7ma", "8va"];
  const ordenCategoriasDamas = ["Suma11", "6ta", "7ma", "8va"];

  const esCategoriaValida = (jugadorCategoria, torneoCategoria, ordenCategorias) => {
    const indiceJugador = ordenCategorias.indexOf(jugadorCategoria);
    const indiceTorneo = ordenCategorias.indexOf(torneoCategoria);

    // El jugador puede inscribirse si su categoría es igual o menor (índice mayor o igual)
    return indiceJugador !== -1 && indiceTorneo !== -1 && indiceJugador <= indiceTorneo;
  };

  if (torneo.genero === "Libres") {
    if (!esCategoriaValida(jugadorLogueado.categoria, torneo.categoria, ordenCategoriasLibres)) {
      setError("No puedes inscribirte en esta categoría. Debes respetar tu categoría o elegir una mayor.");
      return;
    }
  } else if (torneo.genero === "Damas") {
    if (!esCategoriaValida(jugadorLogueado.categoria, torneo.categoria, ordenCategoriasDamas)) {
      setError("No puedes inscribirte en esta categoría. Debes respetar tu categoría o elegir una mayor.");
      return;
    }
  }

  // Permitir que las "Damas" se inscriban en categorías "Libres"
  if (jugadorLogueado.genero === "Damas" && torneo.genero === "Libres") {
    if (!esCategoriaValida(jugadorLogueado.categoria, torneo.categoria, ordenCategoriasLibres)) {
      setError("No puedes inscribirte en esta categoría. Debes respetar tu categoría o elegir una mayor.");
      return;
    }
  }

  // Validar que un jugador de categoría 7ma no pueda jugar en un torneo de categoría 8va
  const indiceJugador = ordenCategoriasLibres.indexOf(jugadorLogueado.categoria);
  const indiceTorneo = ordenCategoriasLibres.indexOf(torneo.categoria);
  if (indiceJugador !== -1 && indiceTorneo !== -1 && indiceJugador < indiceTorneo) {
    setError("No puedes inscribirte en un torneo de una categoría inferior a la tuya.");
    return;
  }

  // Validar que las mujeres no puedan inscribir a un jugador de categoría "Libres" como compañero
  if (jugadorLogueado.genero === "Damas" && compañero.categoria === "Libres") {
    setError("No puedes inscribir a un jugador de categoría 'Libres' como compañero.");
    return;
  }

  // Validar fecha límite de inscripción
    const hoy = new Date().toISOString().split("T")[0];
    if (hoy === torneo.fecha_limite_inscripcion) {
      setError("No puedes inscribirte el día de la fecha límite de inscripción.");
      return;
    }

    // Verificar si ya está inscrito
  const puedeInscribirse = await verificarInscripcion();
  if (!puedeInscribirse) return;

  setStep(2); // Avanzar al paso 2
};

  const confirmarInscripcion = async () => {
  if (
    Object.keys(fechasYHorarios).length !== 2 ||
    Object.values(fechasYHorarios).some((horario) => !horario) ||
    !formaPago
  ) {
    setError("Debes seleccionar 2 fechas y asignar un horario a cada una.");
    return;
  }

  const [fecha1, fecha2] = Object.keys(fechasYHorarios);
  const horario1 = fechasYHorarios[fecha1];
  const horario2 = fechasYHorarios[fecha2];


  Swal.fire({
    title: "¿Confirmar inscripción?",
    text: `Te inscribirás con ${compañero.nombre} en el torneo ${torneo.nombre}.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Sí, confirmar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await api.post(
          "/inscripciones",
          {
            torneoId,
            jugador1Dni: jugadorLogueado.dni,
            jugador2Dni: compañero.dni,
            formaPago,
            fechaSeleccionada: fecha1,
            fechaSeleccionada2: fecha2,
            horario1,
            horario2,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setStep(4); // Avanzar al paso final
        Swal.fire({
            title: "Inscripción Completa",
            text: "La pareja se ha inscrito exitosamente en el torneo. Por favor, comparte el comprobante de transferencia por WhatsApp.",
            icon: "success",
            confirmButtonText: "Aceptar",
        }).then(() => {
          navigate("/"); // Redirigir al home
        });
      } catch (err) {
        setError(err.response?.data?.mensaje || "Error al inscribir la pareja.");
      }
    }
  });
};

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h3>Seleccionar Compañero</h3>
            <label>DNI del Compañero</label>
            <input
              type="text"
              value={dniCompañero}
              onChange={(e) => setDniCompañero(e.target.value)}
              placeholder="Ingrese el DNI del compañero"
              required
            />
            <button type="button" onClick={buscarCompañero}>
              Buscar Compañero
            </button>
            {compañero && (
              <div className="compañero-info">
                <h4>Datos del Compañero</h4>
                <p><strong>Nombre y Apellido:</strong> {compañero.nombre}</p>
                <p><strong>Ciudad:</strong> {compañero.ciudad}</p>
                <p><strong>DNI:</strong> {compañero.dni}</p>
                <p><strong>Categoría:</strong> {compañero.categoria}</p>
                <p><strong>Puntos:</strong> {compañero.puntos}</p>
                <button onClick={confirmarCompañero}>Confirmar Compañero</button>
              </div>
            )}
          </div>
        );
    case 2: // Seleccionar Días
      return (
        <div>
          <h3>Seleccionar Días para Jugar las Zonas</h3>
          <h4>Nota: Solo puedes seleccionar 2 días entre Martes y Viernes</h4>
          <ul className="fechas-list">
  {fechasDisponibles.map((fecha) => {
    const fechaISO = fecha.toISOString();
    return (
      <li key={fechaISO}>
        <label>
          <input
            type="checkbox"
            value={fechaISO}
            checked={fechasYHorarios[fechaISO] !== undefined}
            onChange={() => manejarSeleccionDia(fechaISO)}
          />
          {fecha.toLocaleDateString()}
        </label>
        {fechasYHorarios[fechaISO] !== undefined && (
          <select
            value={fechasYHorarios[fechaISO]}
            onChange={(e) => manejarSeleccionHorario(fechaISO, e.target.value)}
          >
            <option value="">Seleccionar horario</option>
            <option value="18:00">18:00hs</option>
            <option value="19:00">19:00hs</option>
            <option value="20:00">20:00hs</option>
            <option value="21:00">21:00hs</option>
          </select>
        )}
      </li>
    );
  })}
</ul>
          <div>
                          <button onClick={() => setStep(3)}>Confirmar Días y Horarios</button>
          </div>
      </div>
      );
    case 3: // Seleccionar Método de Pago
      return (
        <div>
          <h3>Seleccionar Método de Pago</h3>
          <label>
            <input
              type="radio"
              name="formaPago"
              value="deposito"
              checked={formaPago === "deposito"}
              onChange={(e) => setFormaPago(e.target.value)}
            />
            Transferencia
          </label>
          <button onClick={() => setStep(4)}>Confirmar Método de Pago</button>
        </div>
      );
      case 4:
        return (
          <div>
            <h3>Datos de Pago</h3>
            <p>Tu inscripción está pendiente de pago.</p>
            <p><strong>Datos Bancarios:</strong></p>
            <ul>
              <li><strong>Monto:</strong> ${torneo?.costo_inscripcion}</li>
              <li><strong>Cuenta Bancaria: </strong>3300540725400003363042 {torneo?.cuentaBancaria}</li>
              <li><strong>Alias :   </strong>mutual.evento.sg</li>
            </ul>
          <button onClick={confirmarInscripcion}>Confirmar Inscripción</button>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="inscripcion-container">
      <h2>Inscribirse en {torneo?.nombre}</h2>
      <p><strong>Fecha de Inicio:</strong> {formatearFecha(torneo?.fecha_inicio)}</p>
      <p><strong>Fecha de Fin:</strong> {formatearFecha(torneo?.fecha_fin)}</p>
      <p><strong>Fecha Límite de Inscripción:</strong> {formatearFecha(torneo?.fecha_limite_inscripcion)}</p>
      <p><strong>Categoría:</strong> {torneo?.categoria}</p>
      <p><strong>Costo de Inscripción:</strong> ${torneo?.costo_inscripcion}</p>
      {error && <p className="error-message">{error}</p>}
      {renderStep()}
      <button onClick={() => navigate("/torneos")} className="back-button">Volver a Torneos</button>
    </div>
  );
}